//codex-- Bound report rendering to one server-filtered page and cache recently visited pages.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

//codex-- Keep page size fixed to match the server contract.
const EMPTY_PAGE = { page: 1, page_size: 100, total: 0, total_pages: 1 };
const CACHE_TTL = 30000;

//codex-- Own request cancellation, pagination, totals, permissions, and refreshes in one place.
export default function useWipScheduleList(api, navigate) {
  const [query, setQuery] = useState(() => ({
    month: String(new Date().getMonth() + 1).padStart(2, '0'),
    billing: 'all', search: '', page: 1,
  }));
  const [data, setData] = useState({ schedules: [], can_bill: false, pagination: EMPTY_PAGE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const cache = useRef(new Map());

  //codex-- Criteria changes return to page one; repeating a selection does not refetch.
  const updateQuery = useCallback((changes) => {
    setQuery((previous) => {
      const next = { ...previous, ...changes };
      if (Object.keys(changes).some((key) => key !== 'page' && next[key] !== previous[key])) next.page = 1;
      return Object.keys(next).every((key) => next[key] === previous[key]) ? previous : next;
    });
  }, []);

  //codex-- Writes invalidate cached pages and refresh the current page and its total.
  const reload = useCallback(() => {
    cache.current.clear();
    setRefresh((value) => value + 1);
  }, []);
  const updateRows = useCallback((update) => {
    cache.current.clear();
    setData((previous) => ({ ...previous, schedules: update(previous.schedules) }));
  }, []);

  //codex-- Abort replaced requests and ignore late responses, including requests that ignore cancellation.
  useEffect(() => {
    const key = JSON.stringify([api, query]);
    const cached = cache.current.get(key);
    setError('');
    if (cached && Date.now() - cached.savedAt < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    axios.get(api, {
      params: { getWipScheduleListView: 'all', paginated: 1, page_size: 100, ...query },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      const result = response.data;
      if (!Array.isArray(result?.schedules) || !result.pagination || typeof result.can_bill !== 'boolean') {
        throw new Error('Invalid WIP schedule list response.');
      }
      if (cache.current.size >= 20) cache.current.delete(cache.current.keys().next().value);
      cache.current.set(key, { data: result, savedAt: Date.now() });
      setData(result);
      if (result.pagination.page !== query.page) {
        const adjusted = { ...query, page: result.pagination.page };
        cache.current.set(JSON.stringify([api, adjusted]), { data: result, savedAt: Date.now() });
        setQuery(adjusted);
      }
    }).catch((failure) => {
      if (controller.signal.aborted || failure.code === 'ERR_CANCELED') return;
      const message = failure.response?.data?.error || failure.message || 'Could not load WIP schedules.';
      setError(message);
      if (failure.response?.status === 401) navigate('/login');
      else toast.error(message, { position: 'top-center' });
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [api, navigate, query, refresh]);

  //codex-- Expose the server-filtered rows directly rather than filtering a partial page locally.
  return { query, updateQuery, reload, updateRows, schedules: data.schedules,
    noAccess: !data.can_bill, pagination: data.pagination, loading, error };
}
